// src/utils/stockPlan.js
// D-292 (Stabilization P1) — pure stock maths for one database transaction.
//
// WHY THIS FILE EXISTS
// A business event (store GRN today; issue / return next) can move stock of
// several items, and two lines of the same event can even touch the SAME
// stock record (e.g. one PO lists the same item twice). The old code wrote
// each line in its own transaction, so it never knew the real stock before
// and after the movement (every ledger row showed 0 / 0), and a part-way
// failure left some lines credited and others not.
//
// planStockMovements() works out, in memory and with no Firebase calls:
//   - the real stock_before / stock_after for every movement, in order;
//   - ONE final write per stock record (create or update), so the caller
//     never writes the same document twice in one transaction.
// It throws if any movement would make stock negative — then the caller's
// transaction writes nothing at all.
//
// Pure function → covered by src/utils/stockPlan.test.js (Jest).

// Quantities may be decimals (e.g. 2.5 RM of pipe). Round away float noise
// such as 0.1 + 0.2 = 0.30000000000000004.
export const roundQty = (n) => Math.round(Number(n || 0) * 1e6) / 1e6;

/**
 * @param {Object} states    key → { exists: boolean, data: object|null }
 *                           (what the transaction read for each stock doc)
 * @param {Array}  movements [{ key, company_id, item: {item_id, item_code,
 *                           item_name, category, unit}, stock_change,
 *                           last_rate }]
 * @returns {{ rows: Array<{stock_before:number, stock_after:number}>,
 *             finalWrites: Array<{key:string, isNew:boolean, fields:object}> }}
 */
export const planStockMovements = (states, movements) => {
  if (!Array.isArray(movements) || movements.length === 0) {
    throw new Error("No stock movements to plan");
  }

  const working = {}; // key → running state of that stock record
  const order = [];   // keys in first-seen order (stable output)

  const rows = movements.map((m) => {
    const key = String(m?.key || "");
    if (!key) throw new Error("Stock movement without a stock record");

    const itemId = String(m?.item?.item_id || "").trim();
    if (!itemId) throw new Error("Stock movement without item_id");

    const change = Number(m.stock_change);
    if (!Number.isFinite(change) || change === 0) {
      throw new Error(`Invalid stock change for ${itemId}`);
    }

    let w = working[key];
    if (!w) {
      const s = states?.[key] || { exists: false, data: null };
      w = working[key] = {
        isNew: !s.exists,
        qty: s.exists ? roundQty(s.data?.qty) : 0,
        last_rate: null,
        // Only used when the stock record does not exist yet.
        base: s.exists ? null : {
          company_id: m.company_id || "",
          item_id:    itemId,
          item_code:  m.item?.item_code || "",
          item_name:  m.item?.item_name || "",
          category:   m.item?.category  || "",
          unit:       m.item?.unit      || "",
        },
      };
      order.push(key);
    }

    const stock_before = w.qty;
    const stock_after  = roundQty(stock_before + change);
    if (stock_after < 0) {
      throw new Error(
        `Negative stock blocked for ${itemId} (stock ${stock_before}, change ${change})`
      );
    }
    w.qty = stock_after;

    const rate = Number(m.last_rate);
    if (Number.isFinite(rate) && rate > 0) w.last_rate = rate;

    return { stock_before, stock_after };
  });

  const finalWrites = order.map((key) => {
    const w = working[key];
    const rateField = w.last_rate !== null ? { last_rate: w.last_rate } : {};
    return {
      key,
      isNew: w.isNew,
      fields: w.isNew
        ? { ...w.base, qty: w.qty, defective_stock: 0, scrap_stock: 0, ...rateField }
        : { qty: w.qty, ...rateField },
    };
  });

  return { rows, finalWrites };
};